import React, { useEffect, useMemo, useRef, useState } from "react";
import {
    runClearSheets,
    streamJobLogs,
    getJob,
} from "../../../lib/ligamanagerApi";
import { LEAGUE_OPTIONS } from "../../../lib/leagueSheetsID";

const ACTIONS_MAIN = [
    {
        key: "rx_delete",
        label: "Rx Sheets löschen",
        desc: "Löscht alle Rn_* Ergebnistabs wie RACE, QUALY, PRACTICE, PRO/INT/AM, Combined und Incidents.",
    },
    {
        key: "standings_clear",
        label: "Standings clearen",
        desc: "Leert Standings_Combined, PRO, INT, AM, TEAM und MARKE ab Zeile 2. Header bleibt erhalten.",
    },
    {
        key: "drv_clear",
        label: "drv_history und drv_profile clearen",
        desc: "Leert drv_history und drv_profile ab Zeile 2. Header bleibt erhalten.",
    },
    {
        key: "license_points_only",
        label: "Lizenz (points only)",
        desc: "Entfernt im Lizenz-Tab nur die Punkte zwischen R1 und R12.",
    },
];

const ACTIONS_SEASONSTART = [
    {
        key: "signups_clear",
        label: "Anmeldungen clearen",
        desc: "Leert Anmeldungen bis auf die erste Zeile.",
    },
    {
        key: "withdrawals_clear",
        label: "Abmeldungen clearen",
        desc: "Leert im Tab Abmeldungen nur B2:B.",
    },
    {
        key: "license_full",
        label: "Lizenz (komplett)",
        desc: "Leert A2:A, B2:B und den R1-R12 Bereich im Lizenz-Tab.",
    },
    {
        key: "schedule_clear",
        label: "Schedule clearen",
        desc: "Leert Schedule A2:E13, Header bleibt erhalten.",
    },
    {
        key: "teams_clear",
        label: "Teams clearen",
        desc: "Leert Teams ab Zeile 2, Header bleibt erhalten.",
    },
    {
        key: "drv_clear",
        label: "drv_history und drv_profile clearen",
        desc: "Wie oben, zusätzlich für Saisonstart sichtbar.",
    },
    {
        key: "incidents_clear",
        label: "Incidents clearen",
        desc: "Leert im Tab Incidents den Bereich A2:C. Zeile 1 und Spalte D bleiben unberührt.",
    },
    {
        key: "results_delete",
        label: "Results: alle Standings und Rx Sheets löschen",
        desc: "Löscht alle Standings-Tabs komplett und zusätzlich alle Rx-Tabs.",
    },
];

function prettyErr(e) {
    if (!e) return "Unbekannter Fehler";
    if (typeof e === "string") return e;
    if (e instanceof Error) return e.message || "Fehler";
    if (typeof e === "object") {
        if (e.detail) return typeof e.detail === "string" ? e.detail : JSON.stringify(e.detail, null, 2);
        if (e.message) return e.message;
        try {
            return JSON.stringify(e, null, 2);
        } catch {
            return String(e);
        }
    }
    return String(e);
}

function isDividerMsg(msg) {
    const s = String(msg || "").trim();
    if (!s) return false;
    return /^-+$/.test(s) && s.length >= 20;
}

function classifyLine(msg) {
    const s = String(msg || "");
    if (s.startsWith("[") && s.includes("]")) return "step";
    if (s.startsWith("    ")) return "indent";
    return "normal";
}

function downloadText(filename, text) {
    const blob = new Blob([text], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
}

function downloadJson(filename, obj) {
    const blob = new Blob([JSON.stringify(obj, null, 2)], { type: "application/json;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
}

function ProgressBar({ percent, labelLeft, labelRight }) {
    const p = Math.max(0, Math.min(100, Number(percent || 0)));
    return (
        <div style={{ width: "100%", marginTop: 8 }}>
            <div
                style={{
                    display: "flex",
                    justifyContent: "space-between",
                    opacity: 0.85,
                    fontFamily: "var(--ff-Everett)",
                    fontSize: 12,
                }}
            >
                <div>{labelLeft}</div>
                <div>{labelRight}</div>
            </div>

            <div
                style={{
                    marginTop: 6,
                    height: 10,
                    borderRadius: 999,
                    border: "1px solid rgba(232,253,83,.35)",
                    background: "rgba(232,253,83,.08)",
                    overflow: "hidden",
                }}
            >
                <div
                    style={{
                        height: "100%",
                        width: `${p}%`,
                        background: "rgba(232,253,83,.75)",
                    }}
                />
            </div>
        </div>
    );
}

export default function ClearSheetsPanel() {
    const [sheetIdOrUrl, setSheetIdOrUrl] = useState(LEAGUE_OPTIONS[0]?.value || "");
    const [jobId, setJobId] = useState(null);
    const [runningAction, setRunningAction] = useState("");
    const [actionBusy, setActionBusy] = useState(false);
    const [error, setError] = useState("");

    const [logItems, setLogItems] = useState([]);
    const [selectedActions, setSelectedActions] = useState([]);

    const [showDetails, setShowDetails] = useState(false);
    const [autoScroll, setAutoScroll] = useState(true);
    const [filterInfo, setFilterInfo] = useState(true);
    const [filterWarn, setFilterWarn] = useState(true);
    const [filterError, setFilterError] = useState(true);
    const [filterUi, setFilterUi] = useState(true);

    const [progress, setProgress] = useState({
        status: "",
        step: 0,
        total: 0,
        label: "",
    });

    const stopStreamRef = useRef(null);
    const jobPollTimerRef = useRef(null);
    const scrollBoxRef = useRef(null);
    const stickToBottomRef = useRef(true);

    const busy = actionBusy;

    function pushUiInfo(msg) {
        setLogItems((prev) => [
            ...prev,
            { seq: null, level: "INFO", ts: null, msg: String(msg || ""), uiOnly: true },
        ]);
    }

    function pushLogItem(item) {
        let level = (item?.level || "INFO").toUpperCase();
        if (!["INFO", "WARN", "ERROR"].includes(level)) level = "INFO";

        setLogItems((prev) => [
            ...prev,
            {
                seq: typeof item?.seq === "number" ? item.seq : null,
                level,
                ts: item?.ts ?? null,
                msg: item?.msg ?? "",
                uiOnly: false,
            },
        ]);
    }

    function stopStream() {
        try {
            if (stopStreamRef.current) stopStreamRef.current();
        } catch {}
        stopStreamRef.current = null;
    }

    function stopJobPolling() {
        if (jobPollTimerRef.current) {
            clearInterval(jobPollTimerRef.current);
            jobPollTimerRef.current = null;
        }
    }

    useEffect(() => {
        return () => {
            stopStream();
            stopJobPolling();
        };
    }, []);

    useEffect(() => {
        const el = scrollBoxRef.current;
        if (!el || !autoScroll || !stickToBottomRef.current) return;
        el.scrollTop = el.scrollHeight;
    }, [logItems, autoScroll]);

    function onScrollLogs() {
        const el = scrollBoxRef.current;
        if (!el) return;
        const nearBottom = el.scrollHeight - (el.scrollTop + el.clientHeight) < 40;
        stickToBottomRef.current = nearBottom;
    }

    function resetRunState() {
        setError("");
        setLogItems([]);
        setJobId(null);
        setRunningAction("");
        setProgress({ status: "", step: 0, total: 0, label: "" });
        stopStream();
        stopJobPolling();
    }

    function startJobPolling(id) {
        stopJobPolling();

        const pollOnce = async () => {
            try {
                const j = await getJob(id);
                setProgress({
                    status: j?.status || "",
                    step: Number(j?.progress_step || 0),
                    total: Number(j?.progress_total || 0),
                    label: j?.progress_label || "",
                });

                if (j?.status && ["done", "error", "canceled"].includes(String(j.status))) {
                    stopJobPolling();
                }
            } catch {
                // silent
            }
        };

        pollOnce();
        jobPollTimerRef.current = setInterval(pollOnce, 700);
    }

    function toggleAction(key) {
        setSelectedActions((prev) => {
            if (prev.includes(key)) {
                return prev.filter((x) => x !== key);
            }
            return [...prev, key];
        });
    }

    function uniqueActions(arr) {
        const out = [];
        const seen = new Set();
        for (const v of arr || []) {
            const s = String(v || "").trim();
            if (!s || seen.has(s)) continue;
            seen.add(s);
            out.push(s);
        }
        return out;
    }

    async function startClearSheets() {
        resetRunState();

        if (!sheetIdOrUrl.trim()) {
            setError("Bitte Liga-Sheet auswählen.");
            return;
        }

        const actions = uniqueActions(selectedActions);
        if (!actions.length) {
            setError("Bitte mindestens eine Aktion auswählen.");
            return;
        }

        setActionBusy(true);
        setRunningAction("clear-sheets");

        try {
            pushUiInfo("Aktion wird gestartet: clear-sheets");

            const payload = {
                sheetInput: sheetIdOrUrl.trim(),
                actions,
            };

            const r = await runClearSheets(payload);

            if (!r?.job_id) {
                throw new Error("Backend hat kein job_id zurückgegeben (clear-sheets).");
            }

            setJobId(r.job_id);
            pushUiInfo(`Job gestartet: #${r.job_id}`);
            pushUiInfo(`Ausgewählte Aktionen: ${actions.join(", ")}`);
            pushUiInfo("Live Logs verbinden …");

            startJobPolling(r.job_id);

            const stopFn = streamJobLogs({
                job_id: r.job_id,
                onLog: (item) => pushLogItem(item),
                onDone: (status) => {
                    pushUiInfo(`Job beendet: ${status}`);
                    if (String(status).trim() === "error") {
                        setError("Job beendet mit Fehler. Siehe Logs.");
                    }
                    setActionBusy(false);
                    stopStream();
                    stopJobPolling();
                },
                onError: (e) => {
                    const msg = prettyErr(e);
                    pushUiInfo(`SSE FEHLER: ${msg}`);
                    setError(msg);
                    setActionBusy(false);
                    stopStream();
                    stopJobPolling();
                },
            });

            stopStreamRef.current = stopFn;
        } catch (e) {
            const msg = prettyErr(e);
            setError(msg);
            pushUiInfo(`FEHLER: ${msg}`);
            setActionBusy(false);
            stopStream();
            stopJobPolling();
        }
    }

    function onClearLogs() {
        setLogItems([]);
        setError("");
    }

    function onDownloadJson() {
        const ts = new Date().toISOString().replace(/[:.]/g, "-");
        const fname = `racevision_clear_sheets_${jobId || "nojob"}_logs_${ts}.json`;

        downloadJson(fname, {
            job_id: jobId,
            running_action: runningAction,
            sheet_input: sheetIdOrUrl,
            actions: uniqueActions(selectedActions),
            logs: logItems,
            progress,
        });
    }

    function onDownloadTxt() {
        const ts = new Date().toISOString().replace(/[:.]/g, "-");
        const fname = `racevision_clear_sheets_${jobId || "nojob"}_logs_${ts}.txt`;

        const lines = logItems.map((it) => {
            if (!showDetails) return String(it.msg ?? "");
            return JSON.stringify(
                { seq: it.seq, level: it.level, ts: it.ts, msg: it.msg, uiOnly: it.uiOnly },
                null,
                0
            );
        });

        downloadText(fname, lines.join("\n"));
    }

    const renderedItems = useMemo(() => {
        const allowLevel = (lvl, uiOnly) => {
            const L = (lvl || "INFO").toUpperCase();
            if (uiOnly) return !!filterUi;
            if (L === "INFO") return !!filterInfo;
            if (L === "WARN") return !!filterWarn;
            if (L === "ERROR") return !!filterError;
            return true;
        };

        return logItems
            .filter((it) => allowLevel(it.level, !!it.uiOnly))
            .map((it, idx) => {
                const msg = String(it.msg ?? "");
                let level = (it.level || "INFO").toUpperCase();
                if (!["INFO", "WARN", "ERROR"].includes(level)) level = "INFO";

                return {
                    key: `${it.seq ?? "x"}-${idx}`,
                    level,
                    ts: it.ts,
                    seq: it.seq,
                    msg,
                    isDivider: isDividerMsg(msg),
                    kind: classifyLine(msg),
                    uiOnly: !!it.uiOnly,
                    rawJson: JSON.stringify(
                        { seq: it.seq, level, ts: it.ts, msg: it.msg, uiOnly: it.uiOnly },
                        null,
                        0
                    ),
                };
            });
    }, [logItems, filterInfo, filterWarn, filterError, filterUi]);

    const filteredCount = renderedItems.length;
    const totalCount = logItems.length;
    const progressPercent =
        progress.total > 0 ? Math.round((Math.max(0, progress.step) / Math.max(1, progress.total)) * 100) : 0;

    const selectedSet = new Set(selectedActions);

    return (
        <div className="card card--withbar">
            <h2 style={{ marginTop: 0 }}>Clear Sheets</h2>

            <div style={{ display: "grid", gap: 12, width: "100%", maxWidth: 1100 }}>
                <div className="callout">
                    <div style={{ fontWeight: 700, marginBottom: 6 }}>Aktionen</div>
                    <div style={{ display: "grid", gap: 6, fontFamily: "var(--ff-Everett)" }}>
                        <div><b>Clear Sheets</b> leert oder löscht definierte Tabs für Reset, Ergebnisse-Entfernung oder Saisonstart.</div>
                        <div>Die Ausführung läuft als Job mit Live-Logs und Fortschritt.</div>
                    </div>
                </div>

                <label style={{ width: "100%" }}>
                    <div style={{ opacity: 0.8, marginBottom: 6, fontFamily: "var(--ff-Everett)" }}>
                        Liga
                    </div>
                    <select
                        className="input"
                        value={sheetIdOrUrl}
                        onChange={(e) => setSheetIdOrUrl(e.target.value)}
                        style={{ width: 260 }}
                        disabled={busy}
                    >
                        {LEAGUE_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                                {opt.label}
                            </option>
                        ))}
                    </select>
                </label>

                <div
                    style={{
                        display: "grid",
                        gap: 10,
                        padding: 14,
                        border: "1px solid rgba(255,255,255,.08)",
                        borderRadius: 12,
                        background: "rgba(255,255,255,.03)",
                    }}
                >
                    <div style={{ fontWeight: 700 }}>Reset / Rennen</div>

                    {ACTIONS_MAIN.map((item) => (
                        <label
                            key={item.key}
                            style={{
                                display: "grid",
                                gridTemplateColumns: "20px 1fr",
                                alignItems: "start",
                                gap: 10,
                                cursor: busy ? "not-allowed" : "pointer",
                                opacity: busy ? 0.7 : 1,
                            }}
                        >
                            <input
                                type="checkbox"
                                checked={selectedSet.has(item.key)}
                                onChange={() => toggleAction(item.key)}
                                disabled={busy}
                                style={{ marginTop: 3 }}
                            />
                            <div>
                                <div style={{ fontWeight: 600 }}>{item.label}</div>
                                <div style={{ opacity: 0.78, fontSize: 13, fontFamily: "var(--ff-Everett)" }}>
                                    {item.desc}
                                </div>
                            </div>
                        </label>
                    ))}
                </div>

                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 12,
                        margin: "4px 0 2px",
                    }}
                >
                    <div style={{ flex: 1, height: 1, background: "rgba(255,255,255,.1)" }} />
                    <div
                        style={{
                            fontWeight: 800,
                            letterSpacing: ".08em",
                            textTransform: "uppercase",
                            color: "var(--accent)",
                            fontSize: 12,
                        }}
                    >
                        Saisonstart
                    </div>
                    <div style={{ flex: 1, height: 1, background: "rgba(255,255,255,.1)" }} />
                </div>

                <div
                    style={{
                        display: "grid",
                        gap: 10,
                        padding: 14,
                        border: "1px solid rgba(255,255,255,.08)",
                        borderRadius: 12,
                        background: "rgba(255,255,255,.03)",
                    }}
                >
                    {ACTIONS_SEASONSTART.map((item, idx) => (
                        <label
                            key={`${item.key}-${idx}`}
                            style={{
                                display: "grid",
                                gridTemplateColumns: "20px 1fr",
                                alignItems: "start",
                                gap: 10,
                                cursor: busy ? "not-allowed" : "pointer",
                                opacity: busy ? 0.7 : 1,
                            }}
                        >
                            <input
                                type="checkbox"
                                checked={selectedSet.has(item.key)}
                                onChange={() => toggleAction(item.key)}
                                disabled={busy}
                                style={{ marginTop: 3 }}
                            />
                            <div>
                                <div style={{ fontWeight: 600 }}>{item.label}</div>
                                <div style={{ opacity: 0.78, fontSize: 13, fontFamily: "var(--ff-Everett)" }}>
                                    {item.desc}
                                </div>
                            </div>
                        </label>
                    ))}
                </div>

                <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                    <button
                        className="button button--secondary"
                        onClick={startClearSheets}
                        disabled={busy || !sheetIdOrUrl.trim() || uniqueActions(selectedActions).length === 0}
                    >
                        {busy ? "Clear Sheets läuft…" : "Clear Sheets starten"}
                    </button>

                    <button
                        className="button"
                        onClick={onClearLogs}
                        disabled={busy || logItems.length === 0}
                    >
                        Logs leeren
                    </button>

                    <button
                        className="button"
                        onClick={onDownloadTxt}
                        disabled={logItems.length === 0}
                    >
                        Logs TXT
                    </button>

                    <button
                        className="button"
                        onClick={onDownloadJson}
                        disabled={logItems.length === 0}
                    >
                        Logs JSON
                    </button>
                </div>

                {jobId && (
                    <div style={{ opacity: 0.9, fontFamily: "var(--ff-Everett)" }}>
                        Job: <b>#{jobId}</b> {runningAction ? <>– Aktion: <code>{runningAction}</code></> : null}
                    </div>
                )}

                {jobId ? (
                    <ProgressBar
                        percent={progressPercent}
                        labelLeft={
                            progress.label
                                ? `${progress.label} (${progress.status || "..."})`
                                : progress.status
                                    ? `Status: ${progress.status}`
                                    : "Status: …"
                        }
                        labelRight={progress.total > 0 ? `${progress.step}/${progress.total}` : `${progressPercent}%`}
                    />
                ) : null}

                {error ? (
                    <div
                        style={{
                            whiteSpace: "pre-wrap",
                            color: "#ffb1b1",
                            border: "1px solid rgba(255,120,120,.35)",
                            background: "rgba(255,80,80,.07)",
                            padding: "10px 12px",
                            borderRadius: 10,
                            fontFamily: "var(--ff-Everett)",
                        }}
                    >
                        {error}
                    </div>
                ) : null}

                <div
                    style={{
                        display: "flex",
                        gap: 14,
                        flexWrap: "wrap",
                        alignItems: "center",
                        opacity: 0.92,
                        fontFamily: "var(--ff-Everett)",
                        fontSize: 13,
                    }}
                >
                    <label style={{ display: "inline-flex", gap: 8, alignItems: "center" }}>
                        <input type="checkbox" checked={showDetails} onChange={(e) => setShowDetails(e.target.checked)} />
                        Details
                    </label>
                    <label style={{ display: "inline-flex", gap: 8, alignItems: "center" }}>
                        <input type="checkbox" checked={autoScroll} onChange={(e) => setAutoScroll(e.target.checked)} />
                        Auto-Scroll
                    </label>
                    <label style={{ display: "inline-flex", gap: 8, alignItems: "center" }}>
                        <input type="checkbox" checked={filterInfo} onChange={(e) => setFilterInfo(e.target.checked)} />
                        INFO
                    </label>
                    <label style={{ display: "inline-flex", gap: 8, alignItems: "center" }}>
                        <input type="checkbox" checked={filterWarn} onChange={(e) => setFilterWarn(e.target.checked)} />
                        WARN
                    </label>
                    <label style={{ display: "inline-flex", gap: 8, alignItems: "center" }}>
                        <input type="checkbox" checked={filterError} onChange={(e) => setFilterError(e.target.checked)} />
                        ERROR
                    </label>
                    <label style={{ display: "inline-flex", gap: 8, alignItems: "center" }}>
                        <input type="checkbox" checked={filterUi} onChange={(e) => setFilterUi(e.target.checked)} />
                        UI
                    </label>

                    <div style={{ opacity: 0.7 }}>
                        Sichtbar: {filteredCount} / {totalCount}
                    </div>
                </div>

                <div
                    ref={scrollBoxRef}
                    onScroll={onScrollLogs}
                    className="logbox"
                    style={{
                        minHeight: 260,
                        maxHeight: 520,
                        overflow: "auto",
                        whiteSpace: "pre-wrap",
                    }}
                >
                    {renderedItems.length === 0 ? (
                        <div style={{ opacity: 0.6 }}>Noch keine Logs.</div>
                    ) : (
                        renderedItems.map((it) => {
                            const color =
                                it.level === "ERROR"
                                    ? "#ff9a9a"
                                    : it.level === "WARN"
                                        ? "#ffd36f"
                                        : it.uiOnly
                                            ? "#9ecbff"
                                            : "inherit";

                            if (it.isDivider) {
                                return (
                                    <div
                                        key={it.key}
                                        style={{
                                            opacity: 0.5,
                                            margin: "6px 0",
                                        }}
                                    >
                                        {it.msg}
                                    </div>
                                );
                            }

                            return (
                                <div
                                    key={it.key}
                                    style={{
                                        color,
                                        paddingLeft: it.kind === "indent" ? 14 : 0,
                                        fontFamily: "var(--ff-Everett)",
                                        margin: "2px 0",
                                    }}
                                    title={showDetails ? it.rawJson : ""}
                                >
                                    {showDetails && it.ts ? `[${it.ts}] ` : ""}
                                    {it.msg}
                                </div>
                            );
                        })
                    )}
                </div>
            </div>
        </div>
    );
}